/**
 * Hook personnalisé pour la validation du SIREN
 * Gère l'appel API pour vérifier le SIREN et détecte les SIREN non diffusibles
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { toast } from "react-toastify";
import { useLazyFetchSocieteFromSiretQuery } from "../redux/slice/api/FO/societeApi";

interface UseSirenValidationParams {
  /** Valeur actuelle du SIREN */
  siren: string | undefined;
  /** Indique si le pays est France ou assimilé */
  isFranceOrAssimile: boolean;
  /** ID de la demande d'inscription (optionnel) */
  idDemande?: number | null;
  /** ID de la société (optionnel) */
  idSociete?: number | null;
  /** Fonction pour définir la valeur d'un champ du formulaire */
  setValue: UseFormSetValue<any>;
  /** Fonction pour déclencher la validation d'un champ (optionnel) */
  trigger?: UseFormTrigger<any>;
  /** Nom du champ de raison sociale SIREN (par défaut "RaisonSocialeSiren") */
  raisonSocialeSirenFieldName?: string;
  /** Nom du champ de raison sociale (par défaut "RaisonSociale") */
  raisonSocialeFieldName?: string;
  /** Afficher les toasts de notification (par défaut true) */
  showToasts?: boolean;
  /** Remplir automatiquement le champ RaisonSociale (par défaut true) */
  fillRaisonSociale?: boolean;
}

interface UseSirenValidationReturn {
  /** Message d'erreur de l'API SIREN (null si pas d'erreur) */
  sirenApiError: string | null;
  /** Indique si une requête SIREN est en cours */
  isLoading: boolean;
}

/**
 * Hook personnalisé pour gérer la validation du SIREN
 * - Appelle l'API pour vérifier le SIREN
 * - Détecte les SIREN non diffusibles ([ND])
 * - Pré-remplit les champs de raison sociale
 * - Affiche les messages d'erreur appropriés
 */
export const useSirenValidation = ({
  siren,
  isFranceOrAssimile,
  idDemande = null,
  idSociete = null,
  setValue,
  trigger,
  raisonSocialeSirenFieldName = "RaisonSocialeSiren",
  raisonSocialeFieldName = "RaisonSociale",
  showToasts = true,
  fillRaisonSociale = true
}: UseSirenValidationParams): UseSirenValidationReturn => {
  const [sirenApiError, setSirenApiError] = useState<string | null>(null);
  const [fetchSocieteFromSiret, { isLoading }] = useLazyFetchSocieteFromSiretQuery();
  
  // Ref pour tracker la valeur précédente du SIREN
  const previousSirenRef = useRef<string | undefined>(undefined);
  // Ref pour tracker si c'est le premier render
  const isFirstRenderRef = useRef(true);

  /**
   * Recherche les informations du SIREN via l'API
   */
  const handleSirenLookup = useCallback(async (sirenValue: string) => {
    if (sirenValue && sirenValue.length === 9 && isFranceOrAssimile) {
      try {
        if (showToasts) {
          toast.info("Recherche des informations SIRET...");
        }
        
        const result = await fetchSocieteFromSiret({ 
          Siret: sirenValue, 
          IdDemande: idDemande,
          IdSociete: idSociete
        }).unwrap();
        
        // Vérifier si l'API a retourné un message d'erreur ou d'avertissement
        if (result.Return_Message && (result.Return_IdType_Message === 0 || result.Return_IdType_Message === 2)) {
          // Afficher le message retourné par l'API (compte actif existe, demande en cours, SIREN inconnu, etc.)
          if (showToasts) {
            if (result.Return_IdType_Message === 0) {
              toast.error(result.Return_Message);
            } else {
              toast.warning(result.Return_Message);
            }
          }
          
          // Stocker l'erreur API
          setSirenApiError(result.Return_Message);
          
          // Vider les champs raison sociale car la recherche n'a pas abouti
          setValue(raisonSocialeSirenFieldName, "", { shouldValidate: false, shouldTouch: false });
          if (fillRaisonSociale) {
            setValue(raisonSocialeFieldName, "", { shouldValidate: false, shouldTouch: false });
          }
          return;
        }
        
        // Si tout est OK et que les données de la société sont présentes
        if (result && result.SocieteSiren) {
          // Vérifier si le SIREN est non diffusible (code/libellé = [ND])
          if (result.SocieteSiren.Nom && result.SocieteSiren.Nom.trim() === "[ND]") {
            const errorMessage = "Les SIRET associés à une unité en diffusion partielle ne sont pas pris en compte par notre site.";
            if (showToasts) {
              toast.error(errorMessage);
            }
            setSirenApiError(errorMessage);
            // Vider les champs raison sociale car le SIREN est non diffusible
            setValue(raisonSocialeSirenFieldName, "", { shouldValidate: false, shouldTouch: false });
            if (fillRaisonSociale) {
              setValue(raisonSocialeFieldName, "", { shouldValidate: false, shouldTouch: false });
            }
            return;
          }
          
          // Effacer l'erreur API
          setSirenApiError(null);
          
          // Pré-remplir les champs avec les données de la société
          if (result.SocieteSiren.Nom) {
            setValue(raisonSocialeSirenFieldName, result.SocieteSiren.Nom, { shouldValidate: true, shouldTouch: true });
            if (fillRaisonSociale) {
              setValue(raisonSocialeFieldName, result.SocieteSiren.Nom, { shouldValidate: true, shouldTouch: true });
            }
          }
          
          // Déclencher la validation du champ SIREN pour qu'il passe au vert
          if (trigger) {
            trigger("Siren");
          }
          
          if (showToasts) {
            toast.success("Informations de la société récupérées avec succès !");
          }
        }
      } catch (error: any) {
        console.error("Erreur lors de la recherche SIRET:", error);
        
        // Afficher le message d'erreur spécifique si disponible
        const errorMessage = error?.data?.Return_Message || 
                            error?.message || 
                            "Impossible de récupérer les informations pour ce SIRET";
        
        if (showToasts) {
          toast.error(errorMessage);
        }
        
        // Stocker l'erreur API
        setSirenApiError(errorMessage);
        
        // Vider les champs raison sociale en cas d'erreur
        setValue(raisonSocialeSirenFieldName, "", { shouldValidate: false, shouldTouch: false });
        if (fillRaisonSociale) {
          setValue(raisonSocialeFieldName, "", { shouldValidate: false, shouldTouch: false });
        }
      }
    }
  }, [
    isFranceOrAssimile, 
    idDemande,
    idSociete,
    fetchSocieteFromSiret, 
    setValue, 
    trigger, 
    raisonSocialeSirenFieldName, 
    raisonSocialeFieldName,
    showToasts,
    fillRaisonSociale
  ]);

  /**
   * Effet pour déclencher la validation du SIREN quand il change
   * N'appelle l'API que si le SIREN a réellement changé (pas au chargement initial)
   */
  useEffect(() => {
    // Lors du premier render, on initialise juste la référence sans appeler l'API
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      previousSirenRef.current = siren;
      return;
    }
    
    // Détecter si le SIREN a vraiment changé
    const sirenHasChanged = previousSirenRef.current !== siren;
    
    // Mettre à jour la référence
    previousSirenRef.current = siren;
    
    // N'appeler l'API que si le SIREN a changé et qu'il est valide
    if (sirenHasChanged && siren && siren.length === 9 && isFranceOrAssimile && showToasts) {
      // Appeler l'API uniquement si showToasts est activé (demande en cours de traitement)
      handleSirenLookup(siren);
    } else if (!siren || siren.length < 9) {
      // Si le SIREN n'a pas 9 chiffres ou est vide, vider les champs et effacer l'erreur API
      setSirenApiError(null);
      // Vider les champs raison sociale uniquement si le SIREN a changé
      if (sirenHasChanged) {
        setValue(raisonSocialeSirenFieldName, "", { shouldValidate: false, shouldTouch: false });
      }
    }
  }, [siren, isFranceOrAssimile, handleSirenLookup, setValue, raisonSocialeSirenFieldName, showToasts]);

  /**
   * Effet pour re-trigger la validation quand sirenApiError change
   */
  useEffect(() => {
    if (siren && siren.length === 9 && trigger) {
      trigger("Siren");
    }
  }, [sirenApiError, siren, trigger]);

  return {
    sirenApiError,
    isLoading
  };
};
